/**
 * useNotifications.ts
 *
 * Fetches reminder-sent notification events from GET /api/notifications.
 * Polls every 5 minutes so the bell stays up-to-date without a page reload.
 *
 * Unread count is derived from comparing each notification's `date` against
 * a `last_seen_at` timestamp stored in localStorage.  Calling `markAllRead()`
 * saves the current time so the badge resets to 0.
 */

import { useQuery, useQueryClient } from "react-query";
import { format, isToday, isYesterday, parseISO } from "date-fns";
import api from "../config/api";

const LS_KEY = "notifications_last_seen_at";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface NotificationEvent {
  id: string;
  type: string; // 'reminder_sent'
  description: string;
  date: string; // ISO string
  meta?: Record<string, any>;
  item_id: string;
  item_name: string;
}

export interface NotificationGroup {
  label: string; // "Today", "Yesterday", "Mar 10, 2026"
  events: NotificationEvent[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Group a flat list of notifications by calendar day, most-recent first. */
export function groupByDay(events: NotificationEvent[]): NotificationGroup[] {
  const buckets = new Map<string, NotificationEvent[]>();

  for (const ev of events) {
    const d = parseISO(ev.date);
    const key = isToday(d)
      ? "Today"
      : isYesterday(d)
        ? "Yesterday"
        : format(d, "MMM d, yyyy");

    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(ev);
  }

  return Array.from(buckets.entries()).map(([label, evs]) => ({
    label,
    events: evs,
  }));
}

/** Count events newer than last_seen_at. */
function countUnread(events: NotificationEvent[]): number {
  const raw = localStorage.getItem(LS_KEY);
  if (!raw) return events.length;
  const lastSeen = new Date(raw).getTime();
  return events.filter((e) => new Date(e.date).getTime() > lastSeen).length;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useNotifications() {
  const queryClient = useQueryClient();

  const query = useQuery<NotificationEvent[]>(
    "notifications",
    async () => {
      const res = await api.get("/notifications");
      return res.data as NotificationEvent[];
    },
    {
      refetchInterval: 5 * 60 * 1000, // re-fetch every 5 minutes
      staleTime: 60_000, // consider data fresh for 1 minute
      refetchOnWindowFocus: true, // refresh when user comes back to tab
    }
  );

  const events = query.data ?? [];
  const groups = groupByDay(events);
  const unreadCount = countUnread(events);

  const markAllRead = () => {
    localStorage.setItem(LS_KEY, new Date().toISOString());
    // Force re-render of unread count by invalidating the query lightly
    queryClient.setQueryData<NotificationEvent[]>(
      "notifications",
      (old) => old ?? []
    );
  };

  return {
    isLoading: query.isLoading,
    events,
    groups,
    unreadCount,
    markAllRead,
    refetch: query.refetch,
  };
}
